import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { NoteAuthor, NoteBody } from '../coachingDashboard/coachingDashboard.types';
import type { RootState } from '../store';

type ChipBase = Readonly<{
  localId: string;
  filename: string;
  size: number;
}>;

export type ChipAttachment =
  | (ChipBase & { status: 'done'; mediaId: number })
  | (ChipBase & { status: 'uploading' | 'error' });

export type RegistryEntry = [src: string, mediaId: number];

export type EditorMode = 'create' | 'edit';

export interface NoteSession {
  id: string;
  mode: EditorMode;
  subjectId: number;
  subjectName: string | null;
  noteId: number | null;
  minimized: boolean;
  body: NoteBody | null;
  chips: ChipAttachment[];
  registry: RegistryEntry[];
  createdAt: string | null;
  lastSavedAt: string | null;
  author: NoteAuthor | null;
}

export interface CoachNoteEditorState {
  sessions: NoteSession[];
}

// How many minimized notes float as pills. Overflow is evicted (its content is
// already autosaved and lives in the notes list), never lost.
export const MAX_MINIMIZED_NOTES = 3;

const initialState: CoachNoteEditorState = { sessions: [] };

const findSession = (state: CoachNoteEditorState, id: string): NoteSession | undefined =>
  state.sessions.find((s) => s.id === id);

// Minimize the open session so only one modal shows. A create draft that was
// never typed into (and never saved) is dropped rather than kept as a blank pill.
const stashOpenSessions = (state: CoachNoteEditorState): void => {
  state.sessions = state.sessions.filter((s) => {
    if (s.minimized) return true;
    if (s.mode === 'create' && s.noteId == null && s.body == null) return false;

    s.minimized = true;

    return true;
  });
};

const enforceMinimizedCap = (state: CoachNoteEditorState): void => {
  const minimized = state.sessions.filter((s) => s.minimized);
  const overflow = minimized.length - MAX_MINIMIZED_NOTES;

  if (overflow <= 0) return;

  const evicted = new Set(minimized.slice(0, overflow).map((s) => s.id));

  state.sessions = state.sessions.filter((s) => !evicted.has(s.id));
};

type OpenCreatePayload = Readonly<{
  id: string;
  subjectId: number;
  subjectName: string | null;
}>;

type OpenEditPayload = Readonly<{
  id: string;
  subjectId: number;
  subjectName: string | null;
  noteId: number;
  body: NoteBody;
  chips: ChipAttachment[];
  registry: RegistryEntry[];
  createdAt: string;
  lastSavedAt: string;
  author: NoteAuthor | null;
}>;

const uuid = (): string => crypto.randomUUID();

export const coachNoteEditorSlice = createSlice({
  name: 'coachNoteEditor',
  initialState,
  reducers: {
    openCreateNote: {
      reducer: (state, action: PayloadAction<OpenCreatePayload>) => {
        stashOpenSessions(state);
        state.sessions.push({
          id: action.payload.id,
          mode: 'create',
          subjectId: action.payload.subjectId,
          subjectName: action.payload.subjectName,
          noteId: null,
          minimized: false,
          body: null,
          chips: [],
          registry: [],
          createdAt: null,
          lastSavedAt: null,
          author: null,
        });
        enforceMinimizedCap(state);
      },
      prepare: (input: { subjectId: number; subjectName: string | null }) => ({
        payload: { id: uuid(), ...input },
      }),
    },
    openEditNote: {
      reducer: (state, action: PayloadAction<OpenEditPayload>) => {
        const {
          id,
          subjectId,
          subjectName,
          noteId,
          body,
          chips,
          registry,
          createdAt,
          lastSavedAt,
          author,
        } = action.payload;
        const existing = state.sessions.find((s) => s.mode === 'edit' && s.noteId === noteId);

        stashOpenSessions(state);

        // A session for this note is already open somewhere — focus it, keep its edits.
        if (existing) {
          existing.minimized = false;

          return;
        }

        state.sessions.push({
          id,
          mode: 'edit',
          subjectId,
          subjectName,
          noteId,
          minimized: false,
          body,
          chips,
          registry,
          createdAt,
          lastSavedAt,
          author,
        });
        enforceMinimizedCap(state);
      },
      prepare: (input: Omit<OpenEditPayload, 'id'>) => ({ payload: { id: uuid(), ...input } }),
    },
    // Promote a create session to an edit session once its note exists on the server.
    setSessionNoteId: (
      state,
      action: PayloadAction<{
        id: string;
        noteId: number;
        createdAt: string;
        lastSavedAt: string;
        author: NoteAuthor | null;
      }>,
    ) => {
      const session = findSession(state, action.payload.id);

      if (!session) return;

      session.noteId = action.payload.noteId;
      session.mode = 'edit';
      session.createdAt = action.payload.createdAt;
      session.lastSavedAt = action.payload.lastSavedAt;
      session.author = action.payload.author;
    },
    markSessionSaved: (state, action: PayloadAction<{ id: string; lastSavedAt: string }>) => {
      const session = findSession(state, action.payload.id);

      if (session) session.lastSavedAt = action.payload.lastSavedAt;
    },
    setDraftBody: (state, action: PayloadAction<{ id: string; body: NoteBody }>) => {
      const session = findSession(state, action.payload.id);

      if (session) session.body = action.payload.body;
    },
    addChip: (state, action: PayloadAction<{ id: string; chip: ChipAttachment }>) => {
      const session = findSession(state, action.payload.id);

      if (session) session.chips.push(action.payload.chip);
    },
    updateChip: (state, action: PayloadAction<{ id: string; chip: ChipAttachment }>) => {
      const session = findSession(state, action.payload.id);

      if (!session) return;

      const index = session.chips.findIndex((c) => c.localId === action.payload.chip.localId);

      if (index !== -1) session.chips[index] = action.payload.chip;
    },
    removeChip: (state, action: PayloadAction<{ id: string; localId: string }>) => {
      const session = findSession(state, action.payload.id);

      if (session)
        session.chips = session.chips.filter((c) => c.localId !== action.payload.localId);
    },
    addRegistryEntry: (state, action: PayloadAction<{ id: string; entry: RegistryEntry }>) => {
      const session = findSession(state, action.payload.id);

      if (session) session.registry.push(action.payload.entry);
    },
    minimizeNote: (state, action: PayloadAction<string>) => {
      const session = findSession(state, action.payload);

      if (!session) return;

      session.minimized = true;
      enforceMinimizedCap(state);
    },
    restoreNote: (state, action: PayloadAction<string>) => {
      stashOpenSessions(state);

      const session = findSession(state, action.payload);

      if (session) session.minimized = false;
    },
    closeNote: (state, action: PayloadAction<string>) => {
      state.sessions = state.sessions.filter((s) => s.id !== action.payload);
    },
  },
});

export const {
  openCreateNote,
  openEditNote,
  setSessionNoteId,
  markSessionSaved,
  setDraftBody,
  addChip,
  updateChip,
  removeChip,
  addRegistryEntry,
  minimizeNote,
  restoreNote,
  closeNote,
} = coachNoteEditorSlice.actions;

const selectSessions = (state: RootState): NoteSession[] => state.coachNoteEditor.sessions;

export const selectOpenNoteSession = createSelector(
  selectSessions,
  (sessions) => sessions.find((s) => !s.minimized) ?? null,
);

export const selectMinimizedNoteSessions = createSelector(selectSessions, (sessions) =>
  sessions.filter((s) => s.minimized),
);
